import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CourierPackageApi } from '@shared/sdk-v1/apis';
import type { CourierPackage } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { getUser } from '../state/authSlice';
import { getCourierId } from '../state/courierSlice';
import { DELIVERED_WINDOW_DAYS } from '../utils/reviewPrompt';

export interface UseDeliveredPackagesReturn {
  packages: CourierPackage[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function buildDeliveredFilter(ownerId: number, courierId: number, now: Date) {
  const since = new Date(now.getTime() - DELIVERED_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
  return {
    where: { and: [{ ownerId }, { courierId }, { status: 'Delivered' }, { deliveredDate: { gte: since } }] },
    fields: { id: true, trackingNo: true, description: true, deliveredDate: true },
    order: 'deliveredDate DESC',
    limit: 20,
  };
}

/** The customer's own packages delivered within the review window, newest first. */
export const useDeliveredPackages = (enabled = true): UseDeliveredPackagesReturn => {
  const user = useSelector(getUser);
  const courierId = useSelector(getCourierId);
  const [packages, setPackages] = useState<CourierPackage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !user?.id || !courierId) return;
    setIsLoading(true);
    setError(null);
    try {
      const api = new CourierPackageApi(apiConfig());
      const result = await api.courierPackageFind({
        filter: JSON.stringify(buildDeliveredFilter(Number(user.id), Number(courierId), new Date())),
      });
      setPackages(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load delivered packages');
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  }, [enabled, user?.id, courierId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { packages, isLoading, isLoaded, error, refresh };
};
